import { useTranslation } from "react-i18next";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { COUNTRIES } from "../data/site";
import { CheckCircle2, Ship, Package, FileCheck2 } from "lucide-react";
import { usePageTitle } from "../lib/usePageTitle";
import { useCountryName } from "../lib/useCountryName";

export default function Export() {
    usePageTitle("meta.export");
    const { t } = useTranslation();
    const countryName = useCountryName();

    const qcPoints = t("export.qc.points", { returnObjects: true });
    const packingPoints = t("export.packing.points", { returnObjects: true });
    const compliance = t("export.compliance.items", { returnObjects: true });

    return (
        <main data-testid="page-export" className="pt-32">
            <section className="px-6 md:px-12 lg:px-20 pt-12 pb-20">
                <Reveal>
                    <p className="text-[10px] uppercase tracking-[0.35em] text-[#C5A059]">
                        {t("export.overline")}
                    </p>
                    <h1 className="mt-6 font-heading text-5xl md:text-6xl lg:text-7xl text-[#E8E3D9] leading-[1.02] tracking-tighter max-w-5xl">
                        {t("export.h1_a")}{" "}
                        <span className="italic text-[#C5A059]">
                            {t("export.h1_b")}
                        </span>
                    </h1>
                    <p className="mt-8 max-w-2xl text-[#A39E93] text-base md:text-lg font-light leading-relaxed">
                        {t("export.intro")}
                    </p>
                </Reveal>
            </section>

            {/* Country grid */}
            <section className="bg-[#1A1A1A] border-y border-[#2C2C2C]">
                <div className="px-6 md:px-12 lg:px-20 py-20 md:py-24">
                    <Reveal>
                        <SectionHeading
                            overline={t("export.reach.overline")}
                            title={t("export.reach.title")}
                        />
                    </Reveal>
                    <div
                        data-testid="export-countries-grid"
                        className="mt-12 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-5 gap-y-1"
                    >
                        {COUNTRIES.map((c) => (
                            <div
                                key={c.code}
                                data-testid={`country-${c.code}`}
                                className="flex items-center gap-3 border-b border-[#2C2C2C] py-2.5"
                            >
                                <img
                                    src={`https://flagcdn.com/h20/${c.code}.png`}
                                    srcSet={`https://flagcdn.com/h40/${c.code}.png 2x`}
                                    alt=""
                                    width={20}
                                    height={14}
                                    loading="lazy"
                                    className="h-3.5 w-auto shrink-0 rounded-[1px] ring-1 ring-[#2C2C2C]"
                                />
                                <span className="text-[#E8E3D9] font-light text-sm truncate">
                                    {countryName(c.code, c.name)}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* QC + PACKING */}
            <section className="px-6 md:px-12 lg:px-20 py-24 md:py-32">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
                    <Reveal>
                        <div className="bg-[#1A1A1A] border-l-2 border-[#C5A059] p-8 md:p-10 h-full">
                            <FileCheck2
                                size={28}
                                strokeWidth={1.25}
                                className="text-[#C5A059]"
                            />
                            <h3 className="mt-8 font-heading text-3xl md:text-4xl text-[#E8E3D9] tracking-tight">
                                {t("export.qc.title")}
                            </h3>
                            <ul className="mt-8 space-y-4">
                                {qcPoints.map((q) => (
                                    <li
                                        key={q}
                                        className="flex gap-4 text-sm text-[#E8E3D9] font-light"
                                    >
                                        <CheckCircle2
                                            size={18}
                                            className="text-[#C5A059] shrink-0 mt-0.5"
                                            strokeWidth={1.25}
                                        />
                                        <span className="leading-relaxed">{q}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Reveal>

                    <Reveal delay={120}>
                        <div className="bg-[#1A1A1A] border-l-2 border-[#C5A059] p-8 md:p-10 h-full">
                            <Package
                                size={28}
                                strokeWidth={1.25}
                                className="text-[#C5A059]"
                            />
                            <h3 className="mt-8 font-heading text-3xl md:text-4xl text-[#E8E3D9] tracking-tight">
                                {t("export.packing.title")}
                            </h3>
                            <ul className="mt-8 space-y-4">
                                {packingPoints.map((q) => (
                                    <li
                                        key={q}
                                        className="flex gap-4 text-sm text-[#E8E3D9] font-light"
                                    >
                                        <CheckCircle2
                                            size={18}
                                            className="text-[#C5A059] shrink-0 mt-0.5"
                                            strokeWidth={1.25}
                                        />
                                        <span className="leading-relaxed">{q}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* COMPLIANCE STRIP */}
            <section className="bg-[#1A1A1A] border-y border-[#2C2C2C]">
                <div className="px-6 md:px-12 lg:px-20 py-20 md:py-24">
                    <Reveal>
                        <div className="flex items-center gap-4">
                            <Ship
                                size={26}
                                strokeWidth={1.25}
                                className="text-[#C5A059]"
                            />
                            <p className="text-[10px] uppercase tracking-[0.35em] text-[#C5A059]">
                                {t("export.compliance.overline")}
                            </p>
                        </div>
                    </Reveal>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-px bg-[#2C2C2C]">
                        {compliance.map((c) => (
                            <div
                                key={c.label}
                                data-testid={`compliance-${c.label.toLowerCase()}`}
                                className="bg-[#0D0D0D] p-6 md:p-8"
                            >
                                <p className="text-[10px] uppercase tracking-[0.3em] text-[#A39E93]">
                                    {c.label}
                                </p>
                                <p className="mt-3 font-heading text-xl text-[#E8E3D9] tracking-tight">
                                    {c.value}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </main>
    );
}
